import { useTracking } from '@act/react-components/tracking';
import { CallDto } from '@act/shared/data-transfer-objects';
import {
  Box,
  Button,
  FlexContainer,
  IconLibrary,
  ReemaThemeV2,
} from '@act/storybook';
import { useMobile } from '@act/utilities/MobileContext';
import useBreakpoint from '@act/utilities/useBreakpoint.hook';
import { CircularProgress, IconButton, Typography } from '@mui/material';
import { useMinimizedCallActions } from './hooks/useMinimizedCallActions.hook';

export interface MinimizedCallBannerProps {
  call: CallDto;
  callTitle: string;
  phoneLabel: string;
  onRestore: () => void;
}

/**
 * Bottom banner shown when the fullscreen call view is minimized.
 *
 * @param props - Call metadata and restore handler.
 * @returns Minimized call footer UI.
 */
export const MinimizedCallBanner = ({
  call,
  callTitle,
  phoneLabel,
  onRestore,
}: MinimizedCallBannerProps) => {
  const { isMobile } = useMobile();
  const { mobile: isNarrowViewport } = useBreakpoint();
  const { generateTrackingProps } = useTracking();
  const {
    isConnected,
    isMainConnectionOnHold,
    isHangingUpGuide,
    isTogglingHold,
    handleHold,
    handleLeaveCall,
  } = useMinimizedCallActions(call);

  const useCompactLayout = isMobile || isNarrowViewport;
  const restoreLabel = useCompactLayout ? 'Call view' : 'Return to call view';

  return (
    <Box
      width="100%"
      padding={useCompactLayout ? '16px' : '32px 24px'}
      boxSizing="border-box"
      backgroundColor={ReemaThemeV2.LIGHT_MODE.palette.common.white}
      border={`1px solid ${ReemaThemeV2.LIGHT_MODE.palette.strokePrimary}`}
      borderRadius="12px 12px 0 0"
      boxShadow="0 1px 2px rgba(0,0,0,0.16), 0 2px 6px rgba(0,0,0,0.08)"
    >
      <FlexContainer
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={useCompactLayout ? 1 : 2}
        width="100%"
        height="auto"
        overflowAllVisible
        flexWrap={useCompactLayout}
      >
        <FlexContainer
          direction="row"
          alignItems="center"
          gap={2}
          flexGrow={1}
          minWidth="0"
          height="auto"
          overflowAllVisible
        >
          <IconLibrary iconName="PhoneCall" color="success" size={32} />
          <FlexContainer
            direction="column"
            gap={1}
            minWidth="0"
            height="auto"
            overflowAllVisible
          >
            <Typography variant="titleLarge" color="text.primary" noWrap>
              {callTitle}
            </Typography>
            {!useCompactLayout && (
              <Typography variant="bodySmall" color="text.secondary" noWrap>
                {phoneLabel}
              </Typography>
            )}
          </FlexContainer>
        </FlexContainer>

        <FlexContainer
          direction="row"
          alignItems="center"
          gap={useCompactLayout ? 1 : 2}
          flexShrink={0}
          height="auto"
          overflowAllVisible
        >
          <IconButton
            {...generateTrackingProps('end-call-button')}
            aria-label="End call"
            onClick={() => {
              void handleLeaveCall();
            }}
            disabled={isHangingUpGuide}
            color="error"
          >
            {isHangingUpGuide ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <IconLibrary iconName="PhoneEnded" size={24} color="white" />
            )}
          </IconButton>

          {isConnected && (
            <Button
              trackingName="hold-call-button"
              variant="outlined"
              {...generateTrackingProps('hold-call-button')}
              aria-label={isMainConnectionOnHold ? 'Resume call' : 'Hold call'}
              startIcon={
                isTogglingHold ? (
                  <CircularProgress size={18} />
                ) : (
                  <IconLibrary
                    iconName={isMainConnectionOnHold ? 'PhonePause' : 'Pause'}
                    size={20}
                  />
                )
              }
              onClick={handleHold}
              disabled={isTogglingHold}
            >
              {useCompactLayout ? '' : isMainConnectionOnHold ? 'Resume' : 'Hold'}
            </Button>
          )}

          <Button
            trackingName="return-to-call-view-button"
            variant="text"
            {...generateTrackingProps('return-to-call-view-button')}
            startIcon={
              <IconLibrary iconName="ArrowUUpLeft" size={20} weight="regular" />
            }
            onClick={onRestore}
          >
            {restoreLabel}
          </Button>
        </FlexContainer>
      </FlexContainer>
    </Box>
  );
};
